<template>
    <div class="relative bg-base">
        <!-- Svetové Slovensko Mentoring Network Banner — hidden for TOP-approved users (and admins) since it's a recruitment pitch for the program -->
        <div
            v-if="!isTopParticipant"
            class="w-screen relative left-1/2 right-1/2 -ml-[50vw] -mr-[50vw] bg-gray-100 dark:bg-surface-card py-8 mt-8"
        >
            <div class="mx-auto max-w-4xl w-full px-4">
                <div class="flex flex-col md:flex-row items-center gap-8">
                    <!-- Photo with modern styling -->
                    <div class="hidden md:block flex-shrink-0 w-40 lg:w-48 self-stretch">
                        <div class="relative h-full overflow-hidden rounded-2xl shadow-lg">
                            <img
                                src="/images/mentoring-network-hero.jpg"
                                alt="Mentoringový network pre svetové Slovensko"
                                class="size-full object-cover"
                            />
                        </div>
                    </div>
                    <!-- Content -->
                    <div class="flex-1 text-center md:text-left">
                        <h3 class="text-xl lg:text-2xl font-bold text-gray-900 dark:text-white mb-2">
                            Mentoringový network pre svetové Slovensko
                        </h3>
                        <p class="text-gray-600 dark:text-gray-300 text-sm lg:text-base mb-4">
                            Hľadáme 100 Slovákov a Sloveniek z celého sveta, ktorí sú pripravení odovzdať svoje skúsenosti a pomôcť rozvíjať ďalšiu generáciu lídrov na Slovensku.
                        </p>
                        <!-- Button -->
                        <div class="flex justify-center md:justify-start">
                            <NuxtLink
                                to="/mentoring-network-pre-svetove-slovensko"
                                class="inline-flex items-center justify-center gap-1 text-primary-600 dark:text-primary-400 font-semibold hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
                            >
                                Zistiť viac
                                <Icon name="heroicons:chevron-right" class="size-4" />
                            </NuxtLink>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <!-- Personalised "Môj mentoring" dashboard — renders for any logged-in user with content -->
        <div>
            <div class="my-6">
                <MentoringDashboard />
            </div>
        </div>

        <!-- Header Section with Map -->
        <div>
            <ListingPageHeader
                :model-value="filtersWithQuery"
                collection="mentors"
                :description="pageDescription"
                :filter-options="filterOptions"
                :filter-sections="mentorFilterSections"
                :is-loading="isLoadingOptions"
                :placeholder="searchPlaceholder"
                prominent-search
                :quick-filters="mentorQuickFilters"
                :search-suggestions="searchSuggestions"
                :title="pageTitle"
                @search="updateSearchQuery"
                @update:model-value="updateFilters"
            >
                <template #results-count>
                    <div class="grid w-full grid-cols-[minmax(0,1fr)_auto] items-center gap-2 sm:flex sm:w-auto sm:justify-start">
                        <FilterSortTabs
                            :model-value="sortMode"
                            :options="mentorSortOptions"
                            @update:model-value="setSortMode"
                        />
                        <CViewToggle v-model="viewMode" />
                    </div>
                </template>
                <template v-if="isAuthenticated" #actions>
                    <!-- "Môj mentoring" button only for users without an actionable dashboard above —
                         when the MentoringDashboard renders, it already provides the "Otvoriť celý prehľad →" link. -->
                    <NuxtLink
                        v-if="!isTopParticipant"
                        to="/profile/mentoring"
                        class="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
                    >
                        <Icon name="heroicons:user-group" class="size-5" />
                        Môj mentoring
                    </NuxtLink>
                    <!-- Mentors manage requests via the dashboard above, so these header CTAs
                         are hidden for them (and "Staň sa mentorom" is moot — they already are one). -->
                    <NuxtLink
                        v-if="pendingRequestsCount > 0 && !isMentor"
                        :to="{ name: 'profile-activity', query: { type: 'mentor', tab: 'confirm' } }"
                        class="inline-flex items-center gap-2 px-6 py-3 border-2 border-orange-400 bg-orange-50 text-orange-700 dark:border-orange-500 dark:bg-orange-900/30 dark:text-orange-300 rounded-full font-medium hover:bg-orange-100 dark:hover:bg-orange-900/50 transition-colors"
                    >
                        <Icon name="heroicons:bell-alert" class="size-5" />
                        {{ pendingRequestsCount }} {{ pendingRequestsCount === 1 ? 'žiadosť' : 'žiadosti' }}
                    </NuxtLink>
                    <NuxtLink
                        v-else-if="!hasPublishedMentorProfile && !isMentor"
                        to="/profile/mentoring/mentor"
                        class="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 dark:border-default text-gray-700 dark:text-gray-300 rounded-full font-medium hover:border-gray-400 transition-colors dark:hover:border-default"
                    >
                        <Icon name="heroicons:academic-cap" class="size-5" />
                        Staň sa mentorom
                    </NuxtLink>
                </template>
            </ListingPageHeader>

            <!-- TOP-only toggle banner -->
            <div
                v-if="showTopOnly || isTopParticipant"
                class="mt-4 flex items-center justify-between gap-3 rounded-lg px-4 py-3"
                :class="showTopOnly
                    ? 'bg-teal-50 dark:bg-teal-900/30 border border-teal-200 dark:border-teal-700'
                    : 'bg-gray-50 dark:bg-surface-card border border-gray-200 dark:border-default'"
            >
                <div class="flex items-center gap-2 text-sm">
                    <Icon
                        :name="showTopOnly ? 'heroicons:star-solid' : 'heroicons:star'"
                        class="size-4"
                        :class="showTopOnly ? 'text-teal-600 dark:text-teal-400' : 'text-gray-400'"
                    />
                    <span
                        :class="showTopOnly
                            ? 'text-teal-700 dark:text-teal-300'
                            : 'text-gray-600 dark:text-gray-400'"
                    >
                        {{ showTopOnly ? `Zobrazuješ ${audienceNounGen} z Mentoringového networku pre svetové Slovensko` : `Zobrazuješ všetkých ${audienceNounGen}` }}
                    </span>
                </div>
                <button
                    class="text-sm font-medium transition-colors"
                    :class="showTopOnly
                        ? 'text-teal-600 hover:text-teal-800 dark:text-teal-400 dark:hover:text-teal-200'
                        : 'text-gray-600 hover:text-gray-900 dark:text-gray-400 dark:hover:text-gray-200'"
                    @click="toggleTopOnly"
                >
                    {{ showTopOnly ? 'Zobraziť všetkých' : 'Zobraziť len TOP' }}
                </button>
            </div>

            <!-- AI selection info + Map toggle row -->
            <div class="mb-3 mt-4 flex flex-wrap items-center justify-between gap-2">
                <!-- Left: Mentor count -->
                <div v-if="searchResults" class="flex items-center gap-2 text-sm text-gray-600 dark:text-gray-400">
                    <Icon name="heroicons:sparkles" class="size-4 text-primary-500" />
                    <!-- Logged-in OR has search text: show AI selection -->
                    <span v-if="isAuthenticated || textSearch">
                        <span class="font-semibold text-gray-900 dark:text-white">{{ searchResults.nbHits }}</span>
                        <span v-if="totalMentorCount"> z celkovo {{ totalMentorCount }}</span>
                        {{ audienceNounGen }}
                    </span>
                    <!-- Not logged-in, no search: show total only -->
                    <span v-else>
                        <span class="font-semibold text-gray-900 dark:text-white">{{
                            totalMentorCount || searchResults.nbHits
                        }}</span>
                        mentorov
                    </span>
                </div>

                <!-- Right: Map toggle -->
                <div class="flex items-center gap-3">
                    <button
                        v-if="locationCounts.length > 0"
                        class="flex items-center gap-2 text-sm font-medium text-gray-600 transition-colors hover:text-primary-600 dark:text-gray-400 dark:hover:text-primary-400"
                        @click="showMap = !showMap"
                    >
                        <Icon :name="showMap ? 'heroicons:chevron-up' : 'heroicons:map'" class="size-4" />
                        {{ showMap ? 'Skryť mapu' : 'Zobraziť na mape' }}
                    </button>
                </div>
            </div>

            <!-- Collapsible Map Section -->
            <transition
                enter-active-class="transition-all duration-300 ease-out"
                enter-from-class="opacity-0 -translate-y-2"
                enter-to-class="opacity-100 translate-y-0"
                leave-active-class="transition-all duration-200 ease-in"
                leave-from-class="opacity-100 translate-y-0"
                leave-to-class="opacity-0 -translate-y-2"
            >
                <div v-if="locationCounts.length > 0 && showMap" class="mt-4 overflow-hidden rounded-xl">
                    <CMentorMap
                        :locations="locationCounts"
                        :selected-city="selectedMapCity"
                        :user-city="applicationStore.userProfile?.geoCity"
                        height="280px"
                        @city-click="handleMapCityClick"
                    />
                </div>
            </transition>
        </div>

        <!-- Main Content -->
        <div>
            <!-- Cards View (Aligned grid) -->
            <div
                v-if="viewMode === 'cards' && searchResultsWithBanner && searchResultsWithBanner.hits && searchResultsWithBanner.hits.length"
                class="mentor-grid"
            >
                <template v-for="item in searchResultsWithBanner.hits" :key="item.id">
                    <!-- Render banner card if it's a banner item -->
                    <CMasonryBannerCard v-if="item._isBanner" />
                    <!-- Otherwise render normal mentor card -->
                    <CMasonryCard v-else :data="item" type="mentor" @tag-click="handleTagClick" />
                </template>
            </div>

            <!-- Rows View (List) -->
            <div
                v-else-if="viewMode === 'rows' && searchResults && searchResults.hits && searchResults.hits.length"
                class="space-y-3"
            >
                <MentorRow
                    v-for="mentor in searchResults.hits"
                    :key="mentor.id"
                    :mentor="mentor"
                />
            </div>

            <!-- Independent v-ifs (not chained to the views above), see FILTER_LISTING_PATTERNS §6 -->
            <CardRowLoadingIndicator v-if="isSearching && !searchResults?.hits?.length" />

            <NothingFound v-if="!isSearching && !searchResults?.hits?.length" search-term="ale žiadneho mentora" />

            <!-- Pagination. "Ďalšia strana" is a real link to ?page=N, so crawlers reach page 2+ -->
            <div v-if="totalPages > 1" class="mt-8 flex flex-wrap items-center justify-center gap-4">
                <CButton
                    v-if="page < totalPages"
                    :disabled="isSearching"
                    :to="listingPageRoute(route.query, page + 1)"
                    variant="primary"
                    @click="onNextPageClick"
                >
                    Ďalšia strana
                </CButton>
                <Paging
                    :value="page"
                    :pages="totalPages"
                    :disabled="isSearching"
                    @input="changePage"
                />
            </div>
        </div>

        <div class="mt-12 space-y-10 pb-4">
            <ListingSeoLinks
                title="Ako funguje mentoring na GROWNi"
                description="Mentor ti poradí s kariérou, výberom školy, vlastným projektom či odborným problémom. Vyber si podľa témy alebo mesta a požiadaj o mentoring priamo cez GROWNi."
                :links="mentorsListingLinks"
            />
            <ListingSeoLinks title="Ďalšie príležitosti na GROWNi" :links="mentoringRelatedLinks" :bordered="false" />
        </div>
    </div>
</template>

<script setup lang="ts">
import type { PagedSearchResults } from '#shared/types/search'
import type { UserProfile } from '#shared/types/User'
import { VisibilityType } from '#shared/types/visibility'
import { RequestStatus } from '#shared/types/ParticipationRequest'
import { useTypesense } from '@/composables/typesense/useTypesense'
import { useExponea } from '@/composables/exponea/useExponea'
import { useHeadMeta } from '@/composables/useHeadMeta'
import { useMentoring } from '@/composables/mentoring/useMentoring'
import { usePersonalizationQuery } from '@/composables/search/usePersonalizationQuery'
import { useUser } from '@/composables/user/useUser'
import { useTopMentoringAccess } from '@/composables/mentoring/useTopMentoringAccess'
import { parseFiltersFromQuery, serializeFiltersIntoQuery } from '@/utils/query'
import CMasonryCard from '@/components/ui/CMasonryCard.vue'
import CMasonryBannerCard from '@/components/ui/CMasonryBannerCard.vue'
import CMasonryGrid from '@/components/ui/CMasonryGrid.vue'
import CMentorMap from '@/components/ui/CMentorMap.vue'
import CViewToggle from '@/components/ui/CViewToggle.vue'
import { useListingViewMode } from '@/composables/search/useListingViewMode'
import MentorRow from '@/components/mentoring/MentorRow.vue'
import MentoringDashboard from '@/components/mentoring/MentoringDashboard.vue'
import ListingPageHeader from '@/components/common/ListingPageHeader.vue'
import ListingSeoLinks from '@/components/common/ListingSeoLinks.vue'
import { mentoringRelatedLinks, mentorsListingLinks } from '@/constants/listingSeoLinks'
import { listingPageRoute, useListingPaginationHead } from '@/composables/head/useListingPaginationHead'
import Paging from '@/components/dashboard/Paging.vue'
import FilterSortTabs from '@/components/filters/components/FilterSortTabs.vue'
import type { FilterSection, QuickFilter } from '@/components/filters/UniversalSearchFilter.vue'
import { getFullName } from '#shared/utils'
import orderBy from 'lodash-es/orderBy'
import { whoIdToLabelMap } from '~~/i18n/mentoring/sk/expertise'

const singleValueKeys = ['q', 'remote']
const multiValueKeys = ['interests', 'who', 'city']
type MentorSearchResultItem = UserProfile | { _isBanner: true; id: string }
type MentorSearchResultsWithBanner = Omit<PagedSearchResults<UserProfile>, 'hits' | 'pagedHits'> & {
    hits: MentorSearchResultItem[]
    pagedHits: Record<number, MentorSearchResultItem[]>
}

// 23 mentors + 1 banner card = 24 total items (divisible by 4, 3, and 2 columns)
const MENTORS_PER_PAGE = 23

const mapFiltersFromRouteQueryToTypesenseFields = (queryFilters: any): Record<string, any> => {
    return {
        'mentorWho': queryFilters.who,
        'mentorInterests': queryFilters.interests,
        'geoCity': queryFilters.city
    }
}

// Fields of a mentor hit read by the card (CMasonryCard, type "mentor") and row (MentorRow) views.
// The SSR search result is serialized into the page payload, which was ~1.3 MB with full hits.
const MENTOR_CARD_FIELDS = [
    'id',
    'slug',
    'firstName',
    'lastName',
    'profileImg',
    'geoCity',
    'geoCountry',
    'jobsText',
    'shortProfile',
    'interests',
    'mentorInterests',
    'mentorProfile',
    'mentorBooked'
]

function toMentorCardHit(hit: UserProfile): UserProfile {
    const source = hit as Record<string, any>
    const cardHit: Record<string, any> = {}
    // Copy only fields that are present: the card falls back from one field to another when one is missing.
    for (const field of MENTOR_CARD_FIELDS) {
        if (field in source) cardHit[field] = source[field]
    }
    if (hit.jobs) {
        cardHit.jobs = hit.jobs.map((job) => ({ position: job.position, org: job.org, endYear: job.endYear }))
    }
    // A few profiles store the photo itself as a base64 data: URL (0.5 MB+ each); show the placeholder instead.
    if (hit.profileImg?.startsWith('data:')) {
        cardHit.profileImg = null
    }
    return cardHit as UserProfile
}

const route = useRoute()
const router = useRouter()
const applicationStore = useApplicationStore()
const { joinSearchResults, serializeFilters } = useTypesense()
const { trackExponeaEvent } = useExponea()
const { getHeadMeta } = useHeadMeta()
const { searchMentors, searchMentorsPersonalized, loadMentorFacets, listMentoringsByMentor } = useMentoring()
const { getMentorQuery } = usePersonalizationQuery()
const { formatLocation } = useUser()
const { isTopParticipant: isTopParticipantInStore } = useTopMentoringAccess()

// Firebase Auth is client-only, so everything that depends on who is signed in (heading, sort tabs,
// header actions, TOP banner, recruitment card, personalized sort) waits until after hydration.
const isMounted = ref(false)
const isTopParticipant = computed(() => isMounted.value && isTopParticipantInStore.value)

// TOP-only toggle — defaults to false for SSR, set to true in onMounted for TOP participants
const showTopOnly = ref(false)

const isSearching = ref(true)
const searchResults = ref<PagedSearchResults<UserProfile> | undefined>(undefined)
const defaultImage = new URL('@/assets/images/user-profile-placeholder.svg', import.meta.url).href
const isLoadingOptions = ref(true)
const filterOptions = ref<Record<string, any[]>>({})
const cityFacets = ref<Record<string, number>>({})

// Sync selectedMapCity with route query
const selectedMapCity = computed(() => (route.query.city as string) || null)

// Use facet data for location counts (all mentors, not just current page)
const locationCounts = computed(() => {
    return Object.entries(cityFacets.value)
        .map(([city, count]) => ({ city, count }))
        .sort((a, b) => b.count - a.count)
})

// Total mentor count from statistics API (same as homepage)
const totalMentorCount = ref(0)

// Pending mentoring requests count (for mentor users)
const pendingRequestsCount = ref(0)

async function loadTotalMentorCount() {
    try {
        const data = await $fetch('/api/homepage/statistics')
        totalMentorCount.value = data.mentorsCount || 0
    } catch (error) {
        console.error('Error fetching total mentor count:', error)
    }
}

async function loadPendingRequestsCount() {
    if (!applicationStore.userId) return

    try {
        const mentorings = await listMentoringsByMentor(applicationStore.userId, {
            status: [RequestStatus.Requested, RequestStatus.InDiscussion]
        })
        pendingRequestsCount.value = mentorings?.length || 0
    } catch (error) {
        console.error('Error fetching pending requests:', error)
    }
}

function toggleTopOnly() {
    showTopOnly.value = !showTopOnly.value
    loadFilterOptions()
    searchMentorsAction()
}

// Filter sections for mentors
const mentorFilterSections: FilterSection[] = [
    {
        key: 'interests',
        label: 'Kľúčové slová',
        icon: 'hashtag',
        placeholder: 'Kľúčové slová',
        displayAs: 'keywords'
    },
    {
        key: 'who',
        label: 'Pre koho hľadám mentora/ku?',
        icon: 'group',
        placeholder: 'Vyber pre koho mentor/ka je'
    },
    {
        key: 'city',
        label: 'Lokalita mentora',
        icon: 'location',
        placeholder: 'Vyber lokalitu mentora'
    }
]

// Quick filters for common mentor searches (removed - using search suggestions instead)
const mentorQuickFilters: QuickFilter[] = []

// Search suggestions (AI-style examples)
const searchSuggestions = [
    { label: 'Chcem začať podnikať', query: 'Chcem začať podnikať' },
    { label: 'Kariérny rozvoj v IT', query: 'Kariérny rozvoj v IT' },
    { label: 'Marketing a sociálne siete', query: 'Marketing a sociálne siete' },
    { label: 'Leadership a vedenie tímu', query: 'Leadership a vedenie tímu' }
]

// Collapsible map state
const showMap = ref(false)

// View mode (cards or rows), remembered across visits
const viewMode = useListingViewMode('mentori')

// Auth-based personalization (see isMounted above)
const isAuthenticated = computed(() => isMounted.value && !!applicationStore.userId)

type SortMode = 'recommended' | 'newest'

const defaultSortMode = computed<SortMode>(() => (isAuthenticated.value ? 'recommended' : 'newest'))

const sortMode = computed<SortMode>(() => {
    const requestedSort = route.query.sort
    if (!isAuthenticated.value && requestedSort === 'recommended') {
        return defaultSortMode.value
    }
    if (requestedSort === 'recommended' || requestedSort === 'newest') {
        return requestedSort
    }
    return defaultSortMode.value
})

const mentorSortOptions = computed(() => {
    const newestOption = { value: 'newest', label: 'Najnovšie', shortLabel: 'Nové' }

    if (!isAuthenticated.value) {
        return [newestOption]
    }

    return [
        { value: 'recommended', label: 'Odporúčané', shortLabel: 'Tip' },
        newestOption
    ]
})

// Role signals — TOP-approved on that side, or a published profile on that side.
const isMentor = computed(
    () =>
        isMounted.value &&
        (!!applicationStore.userProfile?.mentorTopApproved ||
            applicationStore.userProfile?.mentorProfile?.visibility === VisibilityType.published)
)
const isMentee = computed(
    () =>
        isMounted.value &&
        (!!applicationStore.userProfile?.menteeTopApproved ||
            applicationStore.userProfile?.menteeProfile?.visibility === VisibilityType.published)
)

const pageTitle = computed(() => {
    if (!isAuthenticated.value) return 'Nájdi si mentora z praxe'
    // Dual-role sees both; a pure mentor is here to find mentees, not mentors.
    if (isMentor.value && isMentee.value) return 'Mentori a mentees pre teba'
    if (isMentor.value) return 'Mentee pre teba'
    return 'Mentori pre teba'
})

// Noun for the results count + toggle banner, kept in sync with the heading so they don't
// contradict. Only a pure mentor is framed as looking for "mentees"; a dual-role user still
// sees the mentor listing (heading includes "Mentori"), so it stays "mentorov".
const audienceNounGen = computed(() => (isMentor.value && !isMentee.value ? 'mentees' : 'mentorov'))

// Search placeholder follows the same framing: a pure mentor is looking for mentees.
const searchPlaceholder = computed(() =>
    isMentor.value && !isMentee.value
        ? 'Opíš, akého mentee hľadáš...'
        : 'Opíš, s čím ti môže mentor pomôcť...'
)

const pageDescription = computed(() => {
    // No description/subtitle - clean hero layout
    return ''
})

// useHead(() => getHeadMeta(route))

const hasPublishedMentorProfile = computed(() => {
    return applicationStore.userProfile?.mentorProfile?.visibility === VisibilityType.published
})

const textSearch = computed(() => {
    return (route.query.q || '') as string
})

const page = computed(() => {
    return Number(route.query.page) || 1
})

useListingPaginationHead(page)

const totalPages = computed(() => {
    return searchResults.value?.nbPages || 1
})

const filters = computed(() => {
    return parseFiltersFromQuery(route.query, {
        singleValueKeys,
        multiValueKeys
    })
})

// Include q (search query) in filters for the filter component
const filtersWithQuery = computed(() => ({
    ...filters.value,
    q: textSearch.value
}))

const mentorSlug = computed(() => {
    return applicationStore.userProfile?.slug
})

// Insert banner at random position in search results
const searchResultsWithBanner = computed<MentorSearchResultsWithBanner | undefined>(() => {
    if (!searchResults.value || !searchResults.value.hits || searchResults.value.hits.length === 0) {
        return searchResults.value as MentorSearchResultsWithBanner | undefined
    }

    // "Pomôž iným rásť / Staň sa mentorom" recruitment banner is pointless for existing mentors.
    if (isMentor.value) {
        return searchResults.value as MentorSearchResultsWithBanner | undefined
    }

    // Clone the search results to avoid mutating the original
    const resultsClone: MentorSearchResultsWithBanner = {
        ...searchResults.value,
        pagedHits: Object.fromEntries(
            Object.entries(searchResults.value.pagedHits || {}).map(([pageNumber, hits]) => [
                Number(pageNumber),
                [...hits]
            ])
        ),
        hits: [...searchResults.value.hits]
    }

    // Place banner at a fixed early position (will appear in first column, early rows)
    const bannerPosition = 1

    // Create a banner item with a special flag
    const bannerItem: MentorSearchResultItem = { _isBanner: true, id: 'banner-cta' }

    // Insert banner at the specified position
    if (resultsClone.hits.length > bannerPosition) {
        resultsClone.hits.splice(bannerPosition, 0, bannerItem)
    } else {
        // If there are fewer items than the desired position, add at the end
        resultsClone.hits.push(bannerItem)
    }

    // Update pagedHits if it exists
    if (resultsClone.pagedHits) {
        const currentPage = searchResults.value.page || 0
        if (resultsClone.pagedHits[currentPage]) {
            const pagedHitsArray = [...resultsClone.pagedHits[currentPage]]
            if (pagedHitsArray.length > bannerPosition) {
                pagedHitsArray.splice(bannerPosition, 0, bannerItem)
            } else {
                pagedHitsArray.push(bannerItem)
            }
            resultsClone.pagedHits[currentPage] = pagedHitsArray
        }
    }

    return resultsClone
})

watch(
    () => route.query,
    async (newQuery, oldQuery) => {
        const pageChanged = newQuery.page !== oldQuery?.page
        const filtersChanged = JSON.stringify({ ...newQuery, page: undefined }) !== JSON.stringify({ ...oldQuery, page: undefined })

        if (filtersChanged) {
            searchResults.value = undefined
        }

        if (filtersChanged || pageChanged) {
            await searchMentorsAction()
        }
    },
    { deep: true }
)

// Perform initial search on server-side for SSR/SEO
const { data: initialSearchData } = await useAsyncData(
    `mentor-search-${JSON.stringify(route.query)}`,
    async () => {
        const filterValues = mapFiltersFromRouteQueryToTypesenseFields(filters.value)
        const searchTerm = (route.query.q || '') as string
        const sortBy = sortMode.value === 'newest' ? 'createdAt:desc' : undefined

        const results = await searchMentors(searchTerm, {
            page: page.value - 1,
            hitsPerPage: MENTORS_PER_PAGE,
            // AND across filter categories (arrays already OR within a category); `disjunction`
            // made every filter a no-op: `interests:[x] || visibility:=published` matches everyone.
            filters: serializeFilters({
                ...filterValues,
                'visibility': 'published'
            }),
            sort_by: sortBy
        })

        return results
    },
    {
        server: true,
        lazy: false,
        transform: (results) => ({ ...results, hits: results.hits.map(toMentorCardHit) })
    }
)

// Set initial search results from SSR
if (initialSearchData.value) {
    searchResults.value = joinSearchResults(initialSearchData.value, searchResults.value)
    isSearching.value = false
}

onMounted(async () => {
    isMounted.value = true

    // Default to TOP-only view for TOP participants (deferred to onMounted to avoid SSR mismatch)
    if (isTopParticipant.value) {
        showTopOnly.value = true
    }

    await Promise.all([loadFilterOptions(), loadTotalMentorCount(), loadPendingRequestsCount()])

    if (isAuthenticated.value && getMentorQuery.value && sortMode.value === defaultSortMode.value) {
        await searchMentorsAction()
    } else if (!searchResults.value) {
        // If initial data wasn't loaded (shouldn't happen), perform search
        await searchMentorsAction()
    }

    // Scroll to previously viewed card (set by detail page back button / CBackButton)
    scrollToCardFromSession()
})

async function loadFilterOptions() {
    isLoadingOptions.value = true

    try {
        const facets = await loadMentorFacets({
            facets: ['mentorWho', 'mentorInterests', 'geoCity'],
            filters: showTopOnly.value ? 'mentorTopApproved:=true' : ''
        })

        // Store city facets for the map
        cityFacets.value = facets['geoCity'] || {}
        const whoFacets = facets['mentorWho'] || {}
        const hasWhoFacetData = Object.keys(whoFacets).length > 0

        // Sort cities alphabetically (keep original values for filtering)
        const sortedCities = Object.entries(cityFacets.value)
            .filter(([city]) => city && city.trim())
            .sort(([a], [b]) => a.localeCompare(b, 'sk'))
            .map(([city, count]) => ({
                id: city,
                label: city,
                count
            }))

        filterOptions.value = {
            who: Object.values(whoIdToLabelMap).reduce<
                Array<{ id: string; label: string; count: number; disabled: boolean }>
            >((options, who) => {
                if (!who) return options

                options.push({
                    id: who.id,
                    label: who.label,
                    count: whoFacets[who.id] || 0,
                    disabled: hasWhoFacetData ? !whoFacets[who.id] : false
                })

                return options
            }, []),
            interests: Object.keys(facets['mentorInterests'] || {}).map((interest) => ({
                id: interest,
                label: interest
            })),
            city: sortedCities
        }
    } catch (error) {
        console.error('Error loading filter options:', error)
    }

    isLoadingOptions.value = false
}

function trackCTAButtonWithLogin() {
    trackExponeaEvent({
        eventName: 'cta1_klik_stansa_mentorom',
        userId: applicationStore.userId,
        eventProperties: {
            button_text: hasPublishedMentorProfile.value ? 'Upraviť mentorský profil' : 'Staň sa mentorom'
        }
    })
}

function changePage(newPage: number) {
    router.push(listingPageRoute(route.query, newPage))
    trackPageChange(newPage)
}

// The next page replaces the results, so bring the reader back up to them
function onNextPageClick() {
    trackPageChange(page.value + 1)
    if (import.meta.client) {
        window.scrollTo({ top: 0, behavior: 'smooth' })
    }
}

function trackPageChange(newPage: number) {
    trackExponeaEvent({
        eventName: 'mentor_search',
        userId: applicationStore.userId,
        eventProperties: { action: 'change_page', page: newPage }
    })
}

function updateFilters(newFilters: any) {
    console.log('updateFilters called with:', newFilters)
    const { q, ...otherFilters } = newFilters
    const query = serializeFiltersIntoQuery(otherFilters, {
        singleValueKeys,
        multiValueKeys
    })

    // Build clean query object (only include non-empty values)
    const cleanQuery: Record<string, any> = {}
    if (q) cleanQuery.q = q
    Object.entries(query).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
            cleanQuery[key] = value
        }
    })

    if (sortMode.value !== defaultSortMode.value) {
        cleanQuery.sort = sortMode.value
    }

    // Clear all filters - use hard reload to ensure clean state
    if (Object.keys(cleanQuery).length === 0) {
        window.location.href = '/mentori'
        return
    }

    // Update filters via router
    router.push({
        path: '/mentori',
        query: cleanQuery
    })
}

function setSortMode(mode: string) {
    const nextMode = mode as SortMode
    if (nextMode === sortMode.value) return

    router.push({
        path: '/mentori',
        query: {
            ...route.query,
            sort: nextMode === defaultSortMode.value ? undefined : nextMode,
            page: undefined
        }
    })
}

function updateSearchQuery(query: string) {
    router.push({
        name: route.name!,
        query: { ...route.query, q: query || undefined, page: undefined }
    })

    trackExponeaEvent({
        eventName: 'mentor_search',
        userId: applicationStore.userId,
        eventProperties: { action: 'semantic_search', query }
    })
}

async function searchMentorsAction() {
    const filterValues = mapFiltersFromRouteQueryToTypesenseFields(filters.value)
    const shouldShowNewest = sortMode.value === 'newest'

    isSearching.value = true

    try {
        // AND across filter categories (arrays already OR within a category) — see the SSR query above.
        let filterString = serializeFilters({
            ...filterValues,
            'visibility': 'published'
        })

        // Add TOP-only filter when toggle is active
        if (showTopOnly.value) {
            filterString = filterString
                ? `${filterString} && mentorTopApproved:=true`
                : 'mentorTopApproved:=true'
        }

        let results

        if (shouldShowNewest) {
            results = await searchMentors(textSearch.value, {
                page: page.value - 1,
                hitsPerPage: MENTORS_PER_PAGE,
                filters: filterString,
                sort_by: 'createdAt:desc'
            })
        } else {
            const cachedPersonalizationQuery = getMentorQuery.value
            const shouldUsePersonalized = isAuthenticated.value && !textSearch.value && cachedPersonalizationQuery

            if (shouldUsePersonalized) {
                results = await searchMentorsPersonalized(textSearch.value, {
                    page: page.value - 1,
                    hitsPerPage: MENTORS_PER_PAGE,
                    filters: filterString,
                    personalizationQuery: cachedPersonalizationQuery,
                    excludeUserId: applicationStore.userId
                })
            } else if (textSearch.value) {
                results = await searchMentorsPersonalized(textSearch.value, {
                    page: page.value - 1,
                    hitsPerPage: MENTORS_PER_PAGE,
                    filters: filterString,
                    excludeUserId: isAuthenticated.value ? applicationStore.userId : undefined
                })
            } else {
                results = await searchMentors('', {
                    page: page.value - 1,
                    hitsPerPage: MENTORS_PER_PAGE,
                    filters: filterString
                })
            }
        }

        searchResults.value = joinSearchResults(results, searchResults.value)
    } catch (error: any) {
        console.error('Error searching mentors:', error)
    } finally {
        isSearching.value = false
    }
}

function navigateToMentor(mentor: UserProfile) {
    router.push({
        name: 'mentor-id',
        params: { id: mentor.slug || mentor.id }
    })
}

function getMentorCurrentJob(mentor: UserProfile) {
    const currentJob = orderBy(mentor?.jobs ?? [], 'endYear', 'desc')[0]
    return [currentJob?.position, currentJob?.org].filter(Boolean).join(' @ ')
}

function getMentorAttributes(mentor: UserProfile) {
    const location = formatLocation(mentor)
    return location ? [location] : []
}

function getMentorTags(mentor: UserProfile) {
    return mentor?.mentorProfile?.interests?.slice(0, 5) ?? []
}

function handleTagClick(tag: string) {
    // Get current interests filter, ensure it's an array
    const currentInterests = Array.isArray(filters.value.interests)
        ? filters.value.interests
        : filters.value.interests
          ? [filters.value.interests]
          : []

    // Toggle the tag in the interests filter
    const newInterests = currentInterests.includes(tag)
        ? currentInterests.filter((interest: string) => interest !== tag)
        : [...currentInterests, tag]

    // Update the filters
    const newFilters = {
        ...filters.value,
        interests: newInterests
    }

    updateFilters(newFilters)
}

function handleMapCityClick(city: string) {
    // Toggle city filter - if already selected, deselect it
    const newCity = selectedMapCity.value === city ? undefined : city

    router.push({
        query: {
            ...route.query,
            city: newCity,
            page: undefined // Reset to first page when filtering
        }
    })

    trackExponeaEvent({
        eventName: 'mentor_search',
        userId: applicationStore.userId,
        eventProperties: { action: 'map_city_filter', city: newCity || 'cleared' }
    })
}
</script>

<style scoped>
.mentor-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 24px;
    align-items: stretch;
}

@media (min-width: 620px) {
    .mentor-grid {
        grid-template-columns: repeat(2, 1fr);
    }
}

@media (min-width: 920px) {
    .mentor-grid {
        grid-template-columns: repeat(3, 1fr);
    }
}
</style>
